import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { getDocs, orderBy, query, limit, startAfter, startAt, endBefore, limitToLast, onSnapshot } from 'firebase/firestore'

const getNestedValue = (obj, path) => path?.split('.').reduce((acc, part) => acc?.[part], obj)

const fetchPaginatedData = async (dbRef, { direction, startAfterDoc, endBeforeDoc, startAtDoc, pageSize = 10, events = {} }) => {
  const baseQuery = query(dbRef, orderBy('createdAt', 'desc'), limit(pageSize + 1))
  let finalQuery;

  if (direction === 'next' && startAfterDoc) {
    finalQuery = query(baseQuery, startAfter(startAfterDoc));
  } else if (direction === 'prev' && endBeforeDoc) {
    finalQuery = query(
      dbRef,
      orderBy('createdAt', 'desc'),
      endBefore(endBeforeDoc),
      limitToLast(pageSize)
    );
  } else if (direction === 'refresh' && startAtDoc) {
    finalQuery = query(dbRef, orderBy('createdAt', 'desc'), startAt(startAtDoc), limit(pageSize + 1));
  } else {
    finalQuery = baseQuery;
  }

  const snapshot = await getDocs(finalQuery)
  const docs = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
  const isLastBatch = docs.length <= pageSize
  const results = docs.slice(0, pageSize)

  events.onPageChange?.()

  return {
    result: results,
    lastDoc: snapshot.docs[results.length - 1],
    firstDoc: snapshot.docs[0],
    isLastBatch,
  }
}

const TableList = forwardRef(({ name, columns, dbRef, tools = {}, pagination = {} }, ref) => {
  const [tableState, setTableState] = useState({
    data: [],
    firstDoc: null,
    lastDoc: null,
    loading: false,
    end: false,
    currentPage: 1
  })
  const pageSize = pagination.pageSize || 10

  const updateTableState = (updates) => setTableState(prev => ({ ...prev, ...updates }))

  useEffect(() => {
    if (!dbRef) return

    const unsubscribe = onSnapshot(
      query(dbRef, orderBy('createdAt', 'desc'), limit(pageSize + 1)),
      (snapshot) => {
        const changes = snapshot.docChanges()
        if (changes.length === 0) return

        const isStructuralChange = changes.some((change) => change.type === 'added' || change.type === 'removed')
        if (isStructuralChange) {
          // Reset pagination to first page if a record was added or deleted
          updateTableState({
            currentPage: 1,
            data: [],
            firstDoc: null,
            lastDoc: null
          });
          loadData('next');
        } else {
          loadData('refresh');
        }
      }
    );

    return () => unsubscribe()
  }, [dbRef, pageSize])

  useEffect(() => {
    if (dbRef) {
      updateTableState({
        currentPage: 1,
        data: [],
        firstDoc: null,
        lastDoc: null
      })
      loadData('next')
    }
  }, [dbRef])

  const loadData = async (direction) => {
    const { lastDoc, firstDoc, currentPage } = tableState
    updateTableState({ loading: true })

    try {
      const data = await fetchPaginatedData(dbRef, {
        direction,
        startAfterDoc: direction === 'next' ? lastDoc : undefined,
        endBeforeDoc: direction === 'prev' ? firstDoc : undefined,
        // On page 1 there is nothing to anchor to: anchoring at the previous first doc would
        // hide records created since (they sort ahead of it under `createdAt desc`).
        startAtDoc: direction === 'refresh' && currentPage > 1 ? firstDoc : undefined,
        pageSize
      })

      if (data.result.length > 0) {
        updateTableState({
          data: data.result,
          firstDoc: data.firstDoc,
          lastDoc: data.lastDoc,
          end: (direction === 'next' || direction === 'refresh') ? data.isLastBatch : false
        })
      } else {
        updateTableState({ end: true, data: [] })
      }
    } finally {
      updateTableState({ loading: false })
    }
  }

  const handlePageChange = (direction) => {
    const { currentPage, loading, end } = tableState
    const isNext = direction === 'next'
    const canChangePage = isNext ? !end && !loading : currentPage > 1 && !loading

    if (canChangePage) {
      updateTableState({ currentPage: currentPage + (isNext ? 1 : -1) })
      loadData(direction)
    }
  }

  useImperativeHandle(ref, () => ({
    refreshCurrentPage: () => loadData('refresh')
  }))

  const renderColumns = useMemo(() => [
    <th key="serial-number">Sl.No</th>,
    ...(columns?.map(column => <th key={column.key}>{column.title}</th>) || [])
  ], [columns])

  const renderRows = useMemo(() => {
    const { data, currentPage } = tableState
    return data.map((item, index) => (
      <tr key={item.id || index} className="product-list invoice-list border border-defaultborder dark:border-defaultborder/10">
        <td>{((currentPage - 1) * pageSize) + index + 1}</td>
        {columns.map((col, colIndex) => (
          <td key={`${col.key}-${colIndex}`} className={col.key === 'actions' ? 'gap-2' : ''}>
            {col.render ? col.render(getNestedValue(item, col.dataIndex), item) : getNestedValue(item, col.dataIndex)}
          </td>
        ))}
      </tr>
    ))
  }, [tableState.data, columns, tableState.currentPage, pageSize])

  const { loading, data, currentPage, end } = tableState
  const iconClass = tools.icon || 'ri-add-line'

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="xl:col-span-12 col-span-12">
        <div className="box">
          <div className="box-header justify-between">
            <div className="box-title">{name}</div>
            {tools.btnName && (
              <div className="flex">
                <button
                  type="button"
                  onClick={tools.btnClick}
                  className="ti-btn !py-1 !px-2 !text-[0.75rem] !text-white !font-medium bg-primary h-10"
                >
                  <i className={`${iconClass} font-semibold align-middle me-1`} /> {tools.btnName}
                </button>
              </div>
            )}
          </div>
          <div className="box-body">
            <div className="table-responsive mb-4">
              <table className="table whitespace-nowrap table-bordered min-w-full">
                <thead>
                  <tr className="border-b">{renderColumns}</tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={columns.length + 1} className="text-center">Loading...</td></tr>
                  )}
                  {!loading && data.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="text-center">
                        <div className="alert alert-info">No data available</div>
                      </td>
                    </tr>
                  )}
                  {!loading && renderRows}
                </tbody>
              </table>
            </div>
            <nav className="sm:flex items-center justify-between flex-wrap">
              <ul className="ti-pagination mb-0 flex flex-row rounded-sm text-[1rem] !ps-0">
                <li className="page-item">
                  <Link
                    className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]"
                    to="#"
                    onClick={() => handlePageChange('prev')}
                    style={currentPage <= 1 || loading ? { pointerEvents: 'none' } : null}
                  >
                    Previous
                  </Link>
                </li>
                <Link className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]" to="#">
                  {currentPage}
                </Link>
                <li className="page-item">
                  <Link
                    className="page-link !text-primary !py-[0.375rem] !px-[0.75rem]"
                    to="#"
                    onClick={() => handlePageChange('next')}
                    style={end || loading ? { pointerEvents: 'none' } : null}
                  >
                    Next
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  )
})

TableList.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.any).isRequired,
  name: PropTypes.string.isRequired,
  dbRef: PropTypes.object.isRequired,
  pagination: PropTypes.shape({
    pageSize: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
  }),
  tools: PropTypes.shape({
    btnName: PropTypes.string,
    btnIcon: PropTypes.string,
    btnClick: PropTypes.func,
  }),
}

export default TableList